import { useEffect, useId, useRef, useCallback } from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Fragment } from 'react/jsx-runtime';
import useIsMobileSize from 'src/hooks/useIsMobileSize';

import SelectEntityForm from 'src/widgets/SelectEntityForm';

import { FILTER_IDS, FILTER_IDS_MESSAGE_DATE } from '../utils';

import type { IFilterOption, IFilterScheme } from '../utils';

type TFilterFormProps = Pick<IFilterScheme, 'options' | 'onSubmit' | 'defaultValues'>;

const renderFilterItem = (
    {
        id,
        subTitle,
        type,
        selectAllTitle,
        maxItems,
        defaultValues,
        items,
        withClearButton,
        withSubmitButton,
        withCustomForm,
    }: IFilterOption & { withCustomForm?: boolean },
    onSubmit?: TFilterFormProps['onSubmit']
) => {
    return (
        <Box
            key={id}
            display="flex"
            flexDirection="column"
            gap={1}
        >
            {subTitle && (
                <Typography
                    variant="body2"
                    color="secondary"
                >
                    {subTitle}
                </Typography>
            )}
            <SelectEntityForm
                type={type}
                name={String(id)}
                options={items.map(({ value, label }) => ({
                    id: value,
                    name: label,
                }))}
                withClearButton={withClearButton}
                withSubmitButton={withSubmitButton}
                allSelectedText={selectAllTitle}
                maxCount={maxItems}
                defaultValues={defaultValues}
                onSubmit={onSubmit}
                withCustomForm={withCustomForm}
            />
        </Box>
    );
};

export default function FilterForm({ options, onSubmit, defaultValues }: TFilterFormProps) {
    const id = useId();
    const isMobile = useIsMobileSize();

    const methods = useForm({
        defaultValues: defaultValues || {},
    });

    // Для фильтра дат сохраняем значения, чтобы они не сбрасывались при изменении других фильтров
    const isDateFilter = options.some((opt) => opt.id === FILTER_IDS.MESSAGE_DATE);
    const preservedDateValuesRef = useRef<{
        date?: string[];
        dateFrom?: string;
        dateTo?: string;
    } | null>(null);

    useEffect(() => {
        if (isDateFilter) {
            // Получаем текущие значения из формы
            const currentDate = methods.getValues(FILTER_IDS.MESSAGE_DATE);
            const currentDateFrom = methods.getValues('dateFrom');
            const currentDateTo = methods.getValues('dateTo');
            
            // Если в форме есть выбранные даты (не пустые), сохраняем их в ref
            if (currentDate && Array.isArray(currentDate) && currentDate.length > 0 && currentDate[0] !== FILTER_IDS_MESSAGE_DATE.ALL) {
                preservedDateValuesRef.current = {
                    date: currentDate,
                    dateFrom: currentDateFrom,
                    dateTo: currentDateTo,
                };
            }
            
            // При обновлении defaultValues используем сохраненные значения, если они есть
            // Это позволяет сохранить даты при изменении других фильтров
            const valuesToUse = preservedDateValuesRef.current || defaultValues;
            const preservedValues = {
                ...defaultValues,
                [FILTER_IDS.MESSAGE_DATE]: valuesToUse?.[FILTER_IDS.MESSAGE_DATE] || defaultValues?.[FILTER_IDS.MESSAGE_DATE] || [],
                dateFrom: valuesToUse?.dateFrom || defaultValues?.dateFrom || '',
                dateTo: valuesToUse?.dateTo || defaultValues?.dateTo || '',
            };
            
            methods.reset(preservedValues);
        } else {
            // Для остальных фильтров просто сбрасываем с новыми defaultValues
            methods.reset(defaultValues);
        }
    }, [JSON.stringify(defaultValues)]);

    // Отслеживаем выбранный период для фильтра даты
    const selectedDate = useWatch({
        control: methods.control,
        name: FILTER_IDS.MESSAGE_DATE,
    });
    const dateFromValue = useWatch({
        control: methods.control,
        name: 'dateFrom',
    });
    const dateToValue = useWatch({
        control: methods.control,
        name: 'dateTo',
    });
    const isCustomDate = selectedDate?.[0] === FILTER_IDS_MESSAGE_DATE.CUSTOM;
    
    // Ref для предотвращения повторных вызовов onSubmit
    const lastSubmittedRef = useRef<string | null>(null);
    const isInitialMountRef = useRef(true);
    
    // Отслеживаем изменение выбранного периода и применяем фильтр
    useEffect(() => {
        // Пропускаем первый рендер
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            return;
        }
        
        if (isDateFilter && selectedDate && selectedDate.length > 0 && !isMobile) {
            const selectedPeriod = selectedDate[0];
            
            // Если выбран произвольный период, не применяем фильтр до заполнения дат
            if (selectedPeriod === FILTER_IDS_MESSAGE_DATE.CUSTOM) {
                return;
            }
            
            // Проверяем, не отправляли ли уже этот фильтр
            const submitKey = `date:${selectedPeriod}`;
            if (lastSubmittedRef.current === submitKey) {
                return;
            }
            lastSubmittedRef.current = submitKey;
            
            // Для остальных периодов применяем сразу
            const currentValues = methods.getValues();
            onSubmit?.(currentValues);
        }
    }, [selectedDate, isMobile, isDateFilter, onSubmit, methods]);
    
    // Отслеживаем изменение дат и применяем фильтр для произвольного периода
    useEffect(() => {
        if (isDateFilter && isCustomDate && !isMobile && dateFromValue && dateToValue) {
            // Проверяем, не отправляли ли уже этот фильтр
            const submitKey = `custom:${dateFromValue}:${dateToValue}`;
            if (lastSubmittedRef.current === submitKey) {
                return;
            }
            lastSubmittedRef.current = submitKey;
            
            // Применяем фильтр только если обе даты заполнены
            const currentValues = methods.getValues();
            onSubmit?.(currentValues);
        }
    }, [dateFromValue, dateToValue, isCustomDate, isMobile, isDateFilter, onSubmit, methods]);

    return (
        <FormProvider {...methods}>
            <Box
                display="flex"
                flexDirection="column"
                gap="1rem"
            >
                {options.map((item: IFilterScheme['options'][number], index) => {
                    if (Array.isArray(item)) {
                        return (
                            <form
                                key={`${id}_${index}`}
                                onSubmit={methods.handleSubmit(onSubmit)}
                            >
                                {item.map((params: IFilterOption, index) => {
                                    return (
                                        <Fragment key={`${params.id}_${index}`}>
                                            {renderFilterItem({ ...params, withCustomForm: true }, onSubmit)}
                                        </Fragment>
                                    );
                                })}
                                {isMobile && (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        fullWidth
                                    >
                                        Применить
                                    </Button>
                                )}
                            </form>
                        );
                    }

                    // Для фильтра даты добавляем поля ввода дат при выборе "Произвольный период"
                    if (item.id === FILTER_IDS.MESSAGE_DATE) {
                        // Создаем кастомный onSubmit для фильтра дат, который проверяет заполненность дат
                        const handleDateSubmit = (data: Obj) => {
                            const selectedPeriod = data[FILTER_IDS.MESSAGE_DATE]?.[0];
                            
                            // Если выбран произвольный период, проверяем что обе даты заполнены
                            if (selectedPeriod === FILTER_IDS_MESSAGE_DATE.CUSTOM) {
                                const dateFrom = data.dateFrom || methods.getValues('dateFrom');
                                const dateTo = data.dateTo || methods.getValues('dateTo');
                                
                                if (!dateFrom || !dateTo) {
                                    // Если даты не заполнены, не применяем фильтр
                                    return;
                                }
                                
                                // Убеждаемся, что даты передаются в onSubmit
                                onSubmit?.({
                                    ...data,
                                    dateFrom,
                                    dateTo,
                                });
                            } else {
                                // Для остальных периодов применяем как обычно
                                onSubmit?.(data);
                            }
                        };
                        
                        // Для фильтра дат всегда используем handleDateSubmit, чтобы проверять заполненность дат
                        return (
                            <Fragment key={item.id}>
                                {renderFilterItem(item, handleDateSubmit)}
                                {isCustomDate && (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={1}
                                        mt={1}
                                    >
                                        <TextField
                                            type="date"
                                            label="От"
                                            value={dateFromValue || ''}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                methods.setValue('dateFrom', value, { shouldValidate: true });
                                                // Автоматически применяем при изменении даты на десктопе, если обе даты заполнены
                                                if (!isMobile && value && dateToValue) {
                                                    setTimeout(() => {
                                                        const formData = methods.getValues();
                                                        handleDateSubmit(formData);
                                                    }, 100);
                                                }
                                            }}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    borderRadius: 2,
                                                    color: 'var(--lm-text)',
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: 'var(--lm-text)',
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: 'var(--lm-text)',
                                                },
                                            }}
                                        />
                                        <TextField
                                            type="date"
                                            label="До"
                                            value={dateToValue || ''}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                methods.setValue('dateTo', value, { shouldValidate: true });
                                                // Автоматически применяем при изменении даты на десктопе, если обе даты заполнены
                                                if (!isMobile && value && dateFromValue) {
                                                    setTimeout(() => {
                                                        const formData = methods.getValues();
                                                        handleDateSubmit(formData);
                                                    }, 100);
                                                }
                                            }}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    borderRadius: 2,
                                                    color: 'var(--lm-text)',
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: 'var(--lm-text)',
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: 'var(--lm-text)',
                                                },
                                            }}
                                        />
                                        {!isMobile && (
                                            <Button
                                                type="button"
                                                variant="contained"
                                                color="primary"
                                                size="medium"
                                                fullWidth
                                                disabled={!dateFromValue || !dateToValue}
                                                onClick={() => {
                                                    const formData = methods.getValues();
                                                    handleDateSubmit(formData);
                                                }}
                                            >
                                                Применить
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Fragment>
                        );
                    }

                    return <Fragment key={item.id}>{renderFilterItem(item, onSubmit)}</Fragment>;
                })}
            </Box>
        </FormProvider>
    );
}
